import { useAccount } from '../account/store.ts';
import { apiMessage } from '../api/errors.ts';
import { api } from '../api/instance.ts';
import { services } from '../services/index.ts';
import { flushAutosave } from '../state/autosave.ts';
import { useDocument } from '../state/documentStore.ts';
import { ask, toast } from '../state/feedback.ts';
import { db } from '../storage/db.ts';

/** Removes this account's synced projects (and sync state) from this device. */
async function removeSyncedLocalData(userId: string): Promise<void> {
  const rows = await db().projectSync.where('userId').equals(userId).toArray();
  if (rows.some((r) => r.id === useDocument.getState().projectId)) useDocument.getState().close();
  await db().projects.bulkDelete(rows.map((r) => r.id));
  await db().projectSync.bulkDelete(rows.map((r) => r.id));
  await db().blobJobs.where('userId').equals(userId).delete();
  await db().trash.where('userId').equals(userId).delete();
  await db().kv.delete(`sync.cursor.${userId}`);
}

export async function signOutFlow(): Promise<boolean> {
  const choice = await ask({
    title: 'Sign out?',
    message: 'Projects that are already backed up can stay on this device.',
    actions: [
      { label: 'Sign Out', value: 'keep' as const, variant: 'primary' },
      { label: 'Sign Out and Remove Synced Projects', value: 'remove' as const, variant: 'danger' },
      { label: 'Cancel', value: null, variant: 'ghost' },
    ],
  });
  if (!choice) return false;
  await flushAutosave();
  const userId = useAccount.getState().session?.userId;
  await services.sync.syncNow().catch(() => {});
  await services.account.signOut();
  if (choice === 'remove' && userId) await removeSyncedLocalData(userId);
  toast('Signed out');
  return true;
}

export async function deleteAccountFlow(): Promise<boolean> {
  const ok = await ask({
    title: 'Delete your account?',
    message:
      'Your account, cloud projects, renders and workspace memberships are permanently deleted. This can’t be undone. App Store or Google Play subscriptions must be canceled in the store.',
    actions: [
      { label: 'Delete Account', value: true, variant: 'danger' },
      { label: 'Cancel', value: false, variant: 'ghost' },
    ],
  });
  if (!ok) return false;
  const res = await deleteAccountNow();
  if (res.ok) {
    toast(
      res.storeSubscriptionsActive
        ? 'Account deleted. Cancel your store subscription in the App Store or Google Play.'
        : 'Account deleted',
      'success',
    );
  } else toast(res.error, 'error');
  return res.ok;
}

/**
 * Deletes the signed-in account (DELETE /v1/me, already confirmed by the caller) and this
 * browser's synced copies. Local-only projects stay on the device.
 */
export async function deleteAccountNow(): Promise<
  { ok: true; storeSubscriptionsActive: boolean } | { ok: false; error: string }
> {
  const userId = useAccount.getState().session?.userId;
  try {
    const res = await services.account.deleteAccount();
    if (userId) await removeSyncedLocalData(userId);
    return { ok: true, storeSubscriptionsActive: res.storeSubscriptionsActive };
  } catch (e) {
    return {
      ok: false,
      error:
        (e as { code?: string })?.code === 'sole_owner'
          ? 'Transfer or close your company workspace first: you’re its only owner.'
          : apiMessage(e, 'Could not delete the account.'),
    };
  }
}

/** Claims the no-card trial (POST /v1/trial), with the Turnstile proof when the server requires one. */
export async function claimTrialFlow(proof?: { challenge: string; turnstileToken: string }): Promise<boolean> {
  try {
    const challenge = proof?.challenge ?? (await api.attestChallenge()).challenge;
    const res = await api.claimTrial({
      platform: 'web',
      challenge,
      turnstile: proof ? { token: proof.turnstileToken } : undefined,
    });
    await services.account.refreshMe();
    toast(`Trial started: ${res.credits} AI render credits`, 'success');
    return true;
  } catch (e) {
    toast(apiMessage(e, 'Could not start the trial.'), 'error');
    return false;
  }
}
